import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import {
  buildBranchListQueryString,
  normalizePro20PageResponse,
} from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  CreatePublicRelationPayload,
  DeletePublicRelationParams,
  GetPublicRelationByIdParams,
  PublicRelation,
  PublicRelationListParams,
  UpdatePublicRelationPayload,
} from '../types/public-relation.types';
import { normalizePublicRelation } from '../utils/normalize-pr';

export const publicRelationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicRelationsByBranch: builder.query<
      PaginatedResponse<PublicRelation>,
      PublicRelationListParams
    >({
      query: ({ branchCode, page, size, sortBy, sortDirection, search }) => ({
        url: `/prs/branch/${branchCode}${buildBranchListQueryString({
          page,
          size,
          sortBy,
          sortDirection,
          search,
        })}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizePublicRelation,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.PublicRelations, id: branchCode },
      ],
    }),
    getPublicRelationById: builder.query<PublicRelation, GetPublicRelationByIdParams>(
      {
        query: ({ prId }) => ({
          url: `/prs/${String(prId)}`,
          method: 'GET',
        }),
        transformResponse: (response: unknown) => {
          const normalized = normalizePublicRelation(
            normalizePro20Response(response),
          );
          if (!normalized) {
            throw new Error('Invalid public relation response');
          }
          return normalized;
        },
        providesTags: (result) =>
          result
            ? [
                { type: API_TAGS.PublicRelations, id: result.branchCode },
                { type: API_TAGS.PublicRelations, id: String(result.prId) },
              ]
            : [],
      },
    ),
    createPublicRelation: builder.mutation<
      PublicRelation,
      CreatePublicRelationPayload
    >({
      query: (body) => ({
        url: '/prs',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizePublicRelation(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid create public relation response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.PublicRelations, id: branchCode },
      ],
    }),
    updatePublicRelation: builder.mutation<
      PublicRelation,
      UpdatePublicRelationPayload
    >({
      query: ({ prId, ...body }) => ({
        url: `/prs/${String(prId)}`,
        method: 'PUT',
        body: { prId, ...body },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizePublicRelation(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid update public relation response');
        }
        return normalized;
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAGS.PublicRelations, id: result.branchCode },
              { type: API_TAGS.PublicRelations, id: String(result.prId) },
            ]
          : [],
    }),
    deletePublicRelation: builder.mutation<null, DeletePublicRelationParams>({
      query: ({ prId }) => ({
        url: `/prs/${String(prId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, prId }) => [
        { type: API_TAGS.PublicRelations, id: branchCode },
        { type: API_TAGS.PublicRelations, id: String(prId) },
      ],
    }),
    duplicatePublicRelation: builder.mutation<
      PublicRelation,
      DeletePublicRelationParams
    >({
      query: ({ prId }) => ({
        url: `/prs/duplicate/${String(prId)}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizePublicRelation(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid duplicate PR response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, prId }) => [
        { type: API_TAGS.PublicRelations, id: branchCode },
        { type: API_TAGS.PublicRelations, id: String(prId) },
      ],
    }),
  }),
});

export const {
  useGetPublicRelationsByBranchQuery,
  useGetPublicRelationByIdQuery,
  useCreatePublicRelationMutation,
  useUpdatePublicRelationMutation,
  useDeletePublicRelationMutation,
  useDuplicatePublicRelationMutation,
} = publicRelationsApi;
